import { useRef } from "react";
import {
  computeNiceAxisRange,
  type AxisRange,
  type ComputeNiceAxisRangeOptions,
} from "@/shared/charts/utils/compute-nice-axis-range";

export interface UseStableAxisRangeOptions extends ComputeNiceAxisRangeOptions {
  /**
   * Minimum change in bounds (as a fraction of the current axis span) required before
   * the axis adopts new bounds. Keeps the axis from rescaling on every minor data update.
   */
  changeThreshold?: number;
}

/**
 * Computes a "nice" axis range from `values`, but only updates the previously returned
 * range when the data would no longer fit, or when the ideal range has drifted enough
 * to matter. This avoids the axis rescaling (and visually jumping) on every small
 * fluctuation while still adapting once the data genuinely outgrows the current view.
 */
export function useStableAxisRange(
  values: number[],
  options: UseStableAxisRangeOptions = {}
): AxisRange {
  const { changeThreshold = 0.05, ...rangeOptions } = options;
  const previousRef = useRef<AxisRange | null>(null);

  const finiteValues = values.filter((value) => Number.isFinite(value));
  const dataMin = finiteValues.length > 0 ? Math.min(...finiteValues) : 0;
  const dataMax = finiteValues.length > 0 ? Math.max(...finiteValues) : 0;

  const candidate = computeNiceAxisRange(finiteValues, rangeOptions);
  const previous = previousRef.current;

  if (previous == null) {
    previousRef.current = candidate;
    return candidate;
  }

  const previousSpan = previous.max - previous.min || 1;
  const dataOutOfBounds = dataMin < previous.min || dataMax > previous.max;
  const driftedSignificantly =
    Math.abs(candidate.min - previous.min) / previousSpan >= changeThreshold ||
    Math.abs(candidate.max - previous.max) / previousSpan >= changeThreshold;

  if (dataOutOfBounds || driftedSignificantly) {
    previousRef.current = candidate;
    return candidate;
  }

  return previous;
}
